import { notification } from 'antd';
import { useEffect, useMemo } from 'react';
import { educationParallelTextRepresentation } from 'entities/education-class';
import { formatSubjectWithLevel } from 'shared/helpers/utils/subject-display';
import {
  useGetAvailableClassGroupTeachersQuery,
  useGetClassGroupsQuery,
  useGetEducationClassListQuery,
  useGetSubjectsQuery,
  useGetTeacherWorkloadsQuery,
  useUpdateClassGroupMutation,
} from 'shared/api';
import { useTableSearchParams } from 'shared/UI/UIKit/Table';
import { useAppDispatch } from 'shared/services/redux/hooks/use-dispatch';
import { useAppSelector } from 'shared/services/redux/hooks/use-selector';
import type { ClassGroup, ClassGroupFilters } from 'shared/api';
import { classGroupsInitialTableState } from './config';
import { classGroupsTableSelectors } from './selectors';
import { classGroupsTableActions } from './slice';
import { getClassGroupsRequest } from './utils';

export const useClassGroupsTable = () => {
  const dispatch = useAppDispatch();
  const {
    request,
    createVisible,
    generateVisible,
    deletingGroup,
    editingGroup,
    editName,
    editTeacherId,
  } = useAppSelector(classGroupsTableSelectors.getState);

  useEffect(
    () => () => {
      dispatch(classGroupsTableActions.reset());
    },
    [dispatch],
  );

  const { data: groupsPage, isFetching: groupsLoading } = useGetClassGroupsQuery(request);
  const { data: classesPage, isFetching: classesLoading } = useGetEducationClassListQuery({
    size: 1000,
  });
  const { data: subjectsPage, isFetching: subjectsLoading } = useGetSubjectsQuery({
    size: 2000,
    sort: ['name,asc'],
  });
  const { data: teachersPage, isFetching: teachersListLoading } = useGetTeacherWorkloadsQuery({
    size: 1000,
    sort: ['code,asc'],
  });
  const { data: availableTeachers = [], isFetching: availableTeachersLoading } =
    useGetAvailableClassGroupTeachersQuery(
      {
        educationalClassId: editingGroup?.educationalClassId || 0,
        subjectId: editingGroup?.subjectId || 0,
      },
      { skip: !editingGroup },
    );
  const [updateClassGroup, { isLoading: isUpdating }] = useUpdateClassGroupMutation();
  const { tableState, onTableChange, onFilterChange, onItemRemove } = useTableSearchParams(
    classGroupsInitialTableState,
    (nextTableState, nextFilterState) =>
      dispatch(
        classGroupsTableActions.setRequest(
          getClassGroupsRequest(nextTableState, nextFilterState as ClassGroupFilters),
        ),
      ),
    groupsPage?.content.length || 0,
    groupsPage?.totalElements || 0,
  );

  const classes = useMemo(() => classesPage?.content || [], [classesPage]);
  const subjects = useMemo(() => subjectsPage?.content || [], [subjectsPage]);
  const teachers = useMemo(() => teachersPage?.content || [], [teachersPage]);

  const classNames = useMemo(
    () =>
      new Map(
        classes.map((educationClass) => [
          educationClass.id,
          `${educationParallelTextRepresentation[educationClass.parallel]}${
            educationClass.litera
          } класс`,
        ]),
      ),
    [classes],
  );
  const subjectNames = useMemo(
    () =>
      new Map(
        subjects.map((subject) => [
          subject.id,
          formatSubjectWithLevel(subject.name, subject.educationLevel),
        ]),
      ),
    [subjects],
  );
  const teacherNames = useMemo(
    () => new Map(teachers.map((teacher) => [teacher.id, teacher.code])),
    [teachers],
  );

  const classOptions = useMemo(
    () => classes.map(({ id }) => ({ label: classNames.get(id), value: id })),
    [classes, classNames],
  );
  const subjectOptions = useMemo(
    () => subjects.map(({ id }) => ({ label: subjectNames.get(id), value: id })),
    [subjects, subjectNames],
  );
  const teacherOptions = useMemo(
    () => teachers.map(({ id, code }) => ({ label: code, value: id })),
    [teachers],
  );
  const availableTeacherOptions = useMemo(() => {
    const options = availableTeachers.map(({ id, code }) => ({ label: code, value: id }));
    if (editingGroup?.teacherId && !options.some(({ value }) => value === editingGroup.teacherId)) {
      options.unshift({
        value: editingGroup.teacherId,
        label:
          teacherNames.get(editingGroup.teacherId) || `Преподаватель #${editingGroup.teacherId}`,
      });
    }
    return options;
  }, [availableTeachers, editingGroup, teacherNames]);

  const onPaginationChange = (page: number, pageSize: number) => {
    onTableChange(
      {
        ...tableState,
        pagination: { ...tableState.pagination, current: page, pageSize },
      },
      { page: page - 1, limit: pageSize, sort: null, filter: null },
      'paginate',
    );
  };

  const startEdit = (group: ClassGroup) => {
    dispatch(classGroupsTableActions.startEdit(group));
  };

  const cancelEdit = () => {
    dispatch(classGroupsTableActions.cancelEdit());
  };

  const saveEdit = async () => {
    if (!editingGroup) return;

    const name = editName.trim();
    if (!name) {
      notification.warning({ message: 'Название группы не может быть пустым' });
      return;
    }

    const body: { id: number; name?: string; teacherId?: number } = { id: editingGroup.id };
    if (name !== editingGroup.name) body.name = name;
    if (editTeacherId !== editingGroup.teacherId && editTeacherId !== undefined) {
      body.teacherId = editTeacherId;
    }
    if (body.name === undefined && body.teacherId === undefined) {
      cancelEdit();
      return;
    }

    await updateClassGroup(body).unwrap();
    cancelEdit();
  };

  const onDeleteSuccess = () => {
    onItemRemove();
  };

  return {
    state: {
      groups: groupsPage?.content || [],
      total: groupsPage?.totalElements || 0,
      tableState,
      loading: groupsLoading || classesLoading || subjectsLoading || teachersListLoading,
      createVisible,
      generateVisible,
      deletingGroup,
      editingGroup,
      editName,
      editTeacherId,
      isUpdating,
      availableTeachersLoading,
      classOptions,
      subjectOptions,
      teacherOptions,
      availableTeacherOptions,
    },
    lookups: {
      getClassName: (id: number) => classNames.get(id) || `Класс #${id}`,
      getSubjectName: (id: number) => subjectNames.get(id) || `Предмет #${id}`,
      getTeacherName: (id?: number | null) =>
        id ? teacherNames.get(id) || `Преподаватель #${id}` : 'Не назначен',
    },
    methods: {
      onTableChange,
      onPaginationChange,
      onFilterChange,
      showCreate: () => dispatch(classGroupsTableActions.setCreateVisible(true)),
      hideCreate: () => dispatch(classGroupsTableActions.setCreateVisible(false)),
      showGenerate: () => dispatch(classGroupsTableActions.setGenerateVisible(true)),
      hideGenerate: () => dispatch(classGroupsTableActions.setGenerateVisible(false)),
      showDelete: (group: ClassGroup) => dispatch(classGroupsTableActions.setDeletingGroup(group)),
      hideDelete: () => dispatch(classGroupsTableActions.setDeletingGroup(null)),
      onDeleteSuccess,
      startEdit,
      cancelEdit,
      saveEdit,
      setEditName: (name: string) => dispatch(classGroupsTableActions.setEditName(name)),
      setEditTeacherId: (teacherId: number | undefined) =>
        dispatch(classGroupsTableActions.setEditTeacherId(teacherId)),
    },
  };
};
